import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import {
  BaseSetupTab,
  IComponentStateHistory,
  IValueChangedEvent,
  REMOVED,
  ControllerApiService
} from '@nrwl/test-setup';
import {
  CONFIRM_SELECTION,
  DSCP_FLOWS_TYPE,
  MOS_CODEC_CONFIG,
  PM_INTERVAL
} from '../../models/model';
import { TwampTestService } from '../../../twamp-test.service';
import { get, isNil, isEqual } from 'lodash';
import { AGENT_MODEL, DialogService, DropDownConfiguration, DropDownData } from '@nrwl/common-lib';
import { TwampConfigFieldMapping } from '../../models/model';

@Component({
  selector: 'nrwl-twamp-test-advanced',
  templateUrl: './twamp-test-advanced.component.html',
  styleUrls: ['./twamp-test-advanced.component.scss']
})
export class TwampTestAdvancedComponent implements OnInit {
  // eslint-disable-next-line
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @Input() stateHistory?: IComponentStateHistory;
  @ViewChild('currentForm', { static: true }) form: NgForm;
  //eslint-disable-next-line
  @Input() componentId: string;
  @Input() tab: BaseSetupTab;
  selectedDeviceIndex = 0;
  configMapping = TwampConfigFieldMapping;
  dscpFlows = [];
  flowDataSource: MatTableDataSource<DSCP_FLOWS_TYPE>;
  config: any;
  outputs: Array<string>;
  selectedOriginator: any;
  specificFlowsUpdated = false;
  availableFlowNames = [];
  vlanId: number;
  ipAddressType: string;
  txIntervalDropdown = [];
  isDfBitDisabled = false;
  formTooltip = {
    keepAlive: 'This will send keep alive to reflector every 5 minutes',
    percentiles:
      'When Calculate Percentiles is enabled PM Report interval must be 1 or 5 min and flow Tx Interval rates must be 100ms, 1 sec or 2 sec',
    codec:
      'MOS and R-factor will be generated for flows with payload rates within 10% of the displayed rate. Payload rate is derived from frame size and transmit interval. If you select “Generic”, MOS and R-factor will be generated for all flows.',
    reflectorUdpPort:
      'The port used in the test is determined during session initialization. The Control Server may alter the specified value.'
  };
  formField = {
    pmInterval: null,
    codecVal: MOS_CODEC_CONFIG[0].id,
    percentiles: null,
    udpPort: null,
    keepAlive: null,
    reboot: null
  };
  confirmSelection = CONFIRM_SELECTION;
  flows = [];
  agentFlowInfo = {};
  specificAgentFlows = [];
  commonFlows = [];
  selectedIndex = 0;
  previousSelectedIndex = 0;
  pmIntervalDropdown = [];
  commonFlowsColumns = ['dscp', 'padding', 'frame-size', 'TxInterval', 'dfBit', 'add'];
  specificFlowsColumns = [
    'dscp',
    'padding',
    'frame-size',
    'TxInterval',
    'dfBit',
    'reflectorUDP',
    'add'
  ];
  deviceDropDownData = [];
  dropdownConfiguration: DropDownConfiguration;
  selectedDevice: DropDownData;
  selectedDeviceId: string;
  mosCodeConfig = MOS_CODEC_CONFIG;
  agentType = null;
  isPmReterivalEnabled = true;
  esamPMInterval: number;
  constructor(
    public setupService: TwampTestService,
    private readonly dialogService: DialogService,
    public controllerApiService: ControllerApiService
  ) {
    this.flowDataSource = new MatTableDataSource<DSCP_FLOWS_TYPE>([]);
    this.isDfBitDisabled = this.setupService.isDfBitOptDisabled();
    this.txIntervalDropdown = this.setupService.getTxIntervalBasedAgent();
  }
  ngOnInit(): void {
    this.initilizeData();
    this.setupService.tabDataChangeEvents.subscribe(index => {
      if (index === 1) {
        this.restoreFormValues();
        this.getSelectedDestination();
      }
    });
  }

  initilizeData(): void {
    this.restoreFormValues();
    this.getDropDownConfiguration();
    this.getSelectedDestination();
  }

  restoreFormValues(): void {
    this.tab.form = this.form;
    this.config = this.tab.config;
    this.outputs = this.config.def.outputs;
    this.selectedOriginator = this.restoreFieldValues(TwampConfigFieldMapping.SELECTED_ORIGINATOR);
    this.pmIntervalDropdown =
      this.config.options[TwampConfigFieldMapping.PM_INTERVAL]?.value ?? PM_INTERVAL;
    this.agentType = this.selectedOriginator?.getModel();
    this.formField.pmInterval = this.restoreFieldValues(TwampConfigFieldMapping.PM_INTERVAL);
    this.formField.keepAlive = this.restoreFieldValues(TwampConfigFieldMapping.KEEP_ALIVE);
    this.formField.percentiles = this.restoreFieldValues(
      TwampConfigFieldMapping.ENABLE_PERCENTILES
    );
    this.formField.codecVal =
      this.restoreFieldValues(TwampConfigFieldMapping.CODEC) ?? this.formField.codecVal;
    this.formField.udpPort = this.restoreFieldValues(TwampConfigFieldMapping.REFLECTOR_UDP);
    this.formField.reboot = this.restoreFieldValues(TwampConfigFieldMapping.REBOOT);
    this.commonFlows = this.restoreFieldValues(TwampConfigFieldMapping.COMMON_FLOWS);
    this.agentFlowInfo = this.restoreFieldValues(TwampConfigFieldMapping.SPECIFIC_FLOWS);
    this.vlanId = this.restoreFieldValues(TwampConfigFieldMapping.SVLAN_ID);
    this.ipAddressType = this.restoreFieldValues(TwampConfigFieldMapping.IP_ADDRESS_TYPE);
    this.selectedIndex = this.restoreFieldValues(TwampConfigFieldMapping.SELECTED_TAB);
    this.previousSelectedIndex = this.selectedIndex;
    this.esamPMInterval = null;
    if (this.agentType === AGENT_MODEL.JMEP_1G) {
      this.getServerPmReterival();
    }
  }

  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  showFormField(field: string): boolean {
    return this.outputs.includes(field);
  }

  getSelectedDestination(): void {
    const selecteDestination = this.setupService.getDestinationAgent();
    if (selecteDestination?.length) {
      this.deviceDropDownData = this.getDropDownData(selecteDestination);
      this.selectedDevice = this.deviceDropDownData[0];
      this.selectedDeviceId = this.selectedDevice.id;
      this.selecteFlowsBasedOnAgent();
    }
  }

  selecteFlowsBasedOnAgent(): void {
    const selecteAgentFlow = this.agentFlowInfo[this.selectedDeviceId];
    if (selecteAgentFlow) {
      this.specificAgentFlows = selecteAgentFlow;
    } else {
      this.commonFlows[0].name = `Flow ${this.selectedDeviceIndex + 1}-1`;
      this.specificAgentFlows = Object.assign([], this.commonFlows);
    }
  }
  getServerPmReterival(): void {
    if (this.controllerApiService.serverConfig?.['esam-config']) {
      this.isPmReterivalEnabled =
        this.controllerApiService.serverConfig?.['esam-config']?.[
          'esam-traffic-pm-retrieval-enabled'
        ];
      if (this.isPmReterivalEnabled) {
        this.esamPMInterval =
          parseInt(
            this.controllerApiService.serverConfig?.['esam-config']?.[
              'esam-traffic-pm-period-in-minutes'
            ]
          ) * 60;
        this.formField.pmInterval = this.esamPMInterval;
        this.onPMIntervalChange();
      }
    }
  }

  getDropDownConfiguration(): void {
    this.dropdownConfiguration = {
      isDisableRipple: true,
      outline: 'outline',
      isDisableOptionCentering: true,
      className: 'device-dropdown',
      panelClass: 'device-panel',
      placeholderValue: 'Select device',
      isSearchable: true,
      selectTriggerImgSrc: 'images/map-marker.svg',
      isDividerEnableInOptions: true,
      isSelectAll: true,
      isWarningAvailable: true,
      isDisabled: false,
      searchConfiguration: {
        isNameAllowed: true,
        isSubContentAllowed: true,
        isSubContent1Allowed: true,
        isSubDetailsAllowed: true
      },
      enableRowIcons: false
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDropDownData(deviceList: Array<any>): DropDownData[] {
    const dropDownData = [];
    let tempData: DropDownData = {
      value: '',
      key: '',
      subContent: '',
      matOptionprefixMatIcon: '',
      id: '',
      subContent3: null
    };
    deviceList?.map(element => {
      tempData = Object.assign({}, tempData);
      tempData.value = element.name.label;
      tempData.subContent = element.reflectorType.label;
      tempData.key = element.id;
      tempData.id = element.ipAddress.label;
      tempData.subContent3 = element.dataInfo?.port;
      dropDownData.push(tempData);
    });
    return dropDownData;
  }

  // eslint-disable-next-line
  deviceSelection(event: DropDownData): void {
    this.selectedDevice = event;
    this.selectedDeviceId = event.id;
    this.selectedDeviceIndex = this.deviceDropDownData.findIndex(data => data.key === event.key);
    this.selecteFlowsBasedOnAgent();
    this.getFlowNameList();
  }

  // eslint-disable-next-line
  onFlowTabChange(index: any): void {
    if (this.previousSelectedIndex !== this.selectedIndex) {
      this.selectedIndex = this.previousSelectedIndex;
      if (this.previousSelectedIndex === 1 && this.specificFlowsUpdated) {
        const param = {
          id: 'twamp.commonFlows',
          callBackService: this,
          callBackFuntion: this.callBackTabChange,
          callBackFnParams: index
        };
        this.dialogService.showDialog('confirm', 'body', param);
      } else {
        this.callBackTabChange(null, index);
      }
    } else {
      this.selectedIndex = this.previousSelectedIndex;
    }
  }

  // eslint-disable-next-line
  callBackTabChange = (service: any, index: any): void => {
    this.selectedIndex = index;
    let flowIndex = 0;
    this.previousSelectedIndex = this.selectedIndex;
    if (this.selectedIndex === 1) {
      const commonFlows = this.restoreFieldValues(TwampConfigFieldMapping.COMMON_FLOWS);
      //override all selected with selected common flows
      this.deviceDropDownData.forEach((device, deviceIndex) => {
        const individualFlows = commonFlows.map((flow, index) => ({
          ...flow,
          name: `Flow ${deviceIndex + 1}-${index + 1}`
        }));
        flowIndex = flowIndex + commonFlows.length;
        this.agentFlowInfo[device.id] = Object.assign([], individualFlows);
      });
      this.selecteFlowsBasedOnAgent();
    }
    this.onDataEmit(TwampConfigFieldMapping.SELECTED_TAB, this.selectedIndex, true);
  };

  onPMIntervalChange(): void {
    this.onDataEmit(TwampConfigFieldMapping.PM_INTERVAL, this.formField.pmInterval, true);
  }
  onCodecChange(): void {
    this.onDataEmit(TwampConfigFieldMapping.CODEC, this.formField.codecVal, true);
  }

  onKeepAliveChange(): void {
    this.onDataEmit(TwampConfigFieldMapping.KEEP_ALIVE, this.formField.keepAlive, true);
  }

  onRebootChange(): void {
    this.onDataEmit(TwampConfigFieldMapping.REBOOT, this.formField.reboot, true);
  }

  onCalculatePercentilesChange(): void {
    this.onDataEmit(TwampConfigFieldMapping.ENABLE_PERCENTILES, this.formField.percentiles, true);
  }

  onReflectorUDPChange(): void {
    this.onDataEmit(
      TwampConfigFieldMapping.REFLECTOR_UDP,
      this.formField.udpPort,
      this.form.controls['udpPortInput']?.valid
    );
  }

  // eslint-disable-next-line
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  disableCodec(): boolean {
    let initiatorMosEnabled;
    if (!this.selectedOriginator) {
      initiatorMosEnabled = false;
    } else {
      const mosEnabled = get(
        this.selectedOriginator.deviceData,
        'capabilities.twamp-initiator.mos',
        false
      );
      initiatorMosEnabled = mosEnabled === 'true' ? true : false;
    }
    return !initiatorMosEnabled;
  }

  // eslint-disable-next-line
  updateCommonFlows(flowsInfo: any): void {
    this.specificFlowsUpdated = false;
    this.onDataEmit(this.configMapping.COMMON_FLOWS, flowsInfo.flows, flowsInfo.isValidForm);
  }

  // eslint-disable-next-line
  updateSpecificFlows(flowsInfo: any): void {
    const commonFlows = this.restoreFieldValues(TwampConfigFieldMapping.COMMON_FLOWS);
    const commonFlowWithoutName = commonFlows.map(flow => {
      const { name, ...rest } = flow;
      return rest;
    });
    const specificFlowWithoutName = flowsInfo.flows.map(flow => {
      const { name, ...rest } = flow;
      return rest;
    });
    this.specificFlowsUpdated = !isEqual(commonFlowWithoutName, specificFlowWithoutName);
    this.agentFlowInfo[this.selectedDeviceId] = flowsInfo.flows;
    this.getFlowNameList();
    this.onDataEmit(this.configMapping.SPECIFIC_FLOWS, this.agentFlowInfo, flowsInfo.isValidForm);
  }

  getFlowNameList(): void {
    this.availableFlowNames = [];
    for (const key of Object.keys(this.agentFlowInfo)) {
      if (key !== this.selectedDeviceId) {
        this.availableFlowNames = this.agentFlowInfo[key].map(data => data.name);
      }
    }
  }
}
